import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Renders a Facebook-style ad preview for use in campaign creation UIs, showing business name, imagery, promotional text and basic engagement counters.
 * @component
 * @selector app-facebook-ad-preview
 * @example
 *   <app-facebook-ad-preview
 *     [businessName]="'Acme Co.'"
 *     [discountText]="'Limited time offer'"
 *     [discountPercentage]="'20%'"
 *     [ctaText]="'Shop Now'"
 *     [likes]="240"
 *     [comments]="18"
 *     [shares]="5"
 *     [logoUrl]="'/assets/logo.png'"
 *     [imageUrl]="'/assets/ad-image.png'">
 *   </app-facebook-ad-preview>
 * @input {string} businessName - The display name of the business shown in the ad header (default: '').
 * @input {string} discountText - Short promotional text or tagline displayed under the business name (default: '').
 * @input {string} discountPercentage - Highlighted discount or offer percent shown in the creative (default: '').
 * @input {string} ctaText - Call-to-action text shown on the ad button (default: '').
 * @input {number} likes - Number of likes to display on the preview social bar (default: 129).
 * @input {number} comments - Number of comments to display on the preview social bar (default: 12).
 * @input {number} shares - Number of shares to display on the preview social bar (default: 8).
 * @input {string} logoUrl - URL for the business logo used in the ad header (default: '/assets/images/image 29.png').
 * @input {string} imageUrl - URL for the main creative image shown in the ad (default: '/assets/images/Frame 1171276863.png').
 * @output {none} - This component does not emit any outputs; it is a presentational preview component.
 * Notes:
 *   - Standalone component that imports CommonModule and is intended for previewing ad creatives in forms or builders.
 *   - No custom methods or Angular lifecycle hooks are implemented; it relies on Angular's change detection to update when inputs change.
 */
@Component({
  selector: 'app-facebook-ad-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './facebook-ad-preview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./facebook-ad-preview.component.scss'],
})
export class FacebookAdPreviewComponent {
  @Input() businessName = '';
  @Input() discountText = '';
  @Input() discountPercentage = '';
  @Input() ctaText = '';
  @Input() likes = 129;
  @Input() comments = 12;
  @Input() shares = 8;
  @Input() logoUrl = '/assets/images/image 29.png';
  @Input() imageUrl = '/assets/images/Frame 1171276863.png';
}
